import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck, Sparkles, Truck } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 8))).catch(() => {});
  }, []);

  return (
    <div className="bg-background">
      {/* HERO */}
      <section className="grain relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 z-0">
          <img src="https://images.pexels.com/photos/9794458/pexels-photo-9794458.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="" aria-hidden="true" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        </div>
        <div className="relative z-10 mx-auto grid max-w-7xl gap-8 px-5 py-24 sm:px-8 md:grid-cols-12 md:py-32">
          <div className="md:col-span-8">
            <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
              className="overline text-secondary">Premium Trading Cards · Est. 2026</motion.p>
            <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
              className="mt-4 font-display text-4xl font-900 leading-[0.95] tracking-tighter text-white sm:text-5xl lg:text-6xl">
              Chase the<br /><span className="text-primary">rarest pulls</span><br />on the planet.
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.12 }}
              className="mt-6 max-w-lg text-base text-zinc-400">
              Hand-graded Pokémon, sealed product and collectible trading cards. Authenticated, protected and shipped with obsessive care.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.18 }}
              className="mt-9 flex flex-wrap gap-4">
              <Link to="/catalog" data-testid="hero-shop-btn"
                className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 font-medium text-white transition-transform hover:-translate-y-1">
                Shop the drop <ArrowUpRight size={18} />
              </Link>
              <Link to="/catalog?category=Sealed" data-testid="hero-sealed-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 font-medium text-white transition-colors hover:bg-white/5">
                Sealed product
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-b border-white/10 bg-zinc-950">
        <div className="mx-auto grid max-w-7xl grid-cols-1 divide-y divide-white/5 px-5 sm:px-8 md:grid-cols-3 md:divide-x md:divide-y-0">
          {[
            { icon: ShieldCheck, t: "Authenticity Guaranteed", d: "Every card verified before it ships." },
            { icon: Truck, t: "Protected Shipping", d: "Sleeved, toploaded, tracked worldwide." },
            { icon: Sparkles, t: "Collector Grade", d: "Near-mint and graded inventory only." },
          ].map((f) => (
            <div key={f.t} className="flex items-start gap-4 py-7 md:px-8">
              <f.icon className="mt-0.5 text-secondary" size={22} />
              <div>
                <p className="font-display text-sm font-700 text-white">{f.t}</p>
                <p className="mt-1 text-sm text-zinc-500">{f.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
        <div className="mb-10 flex items-end justify-between">
          <div>
            <p className="overline text-secondary">Hand-picked</p>
            <h2 className="mt-2 font-display text-3xl font-800 tracking-tight text-white">Featured Cards</h2>
          </div>
          <Link to="/catalog" data-testid="view-all-btn" className="text-sm text-zinc-400 transition-colors hover:text-white">View all →</Link>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-4">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>
    </div>
  );
}
